import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { outlineApi } from '../../api/outlines';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import './Outline.css';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';
import { usePaidConfirm } from '../Common/usePaidConfirm';

export function SectionDetail({
  section,
  onUpdate,
  onDelete,
  onCreateWorkshop
}) {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const { confirm, modalProps } = useConfirm();
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const navigate = useNavigate();

  useEffect(() => {
    if (section?.id) {
      loadWorkshops();
    }
  }, [section?.id]);

  const loadWorkshops = async () => {
    try {
      setLoading(true);
      const response = await outlineApi.getWorkshops(section.id);
      setWorkshops(response.data || []);
    } catch (err) {
      console.error('Failed to load workshops:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const ok = await askPaid('outline_section');
    if (!ok) return;
    try {
      setGenerating(true);
      setError(null);
      await outlineApi.generateSection(section.id);
      onUpdate();
    } catch (err) {
      setError('Failed to generate content');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: "Delete section?", message: `Delete "${section.display_name}"?`, confirmLabel: "Delete", destructive: true });
    if (!ok) return;
    try {
      await outlineApi.deleteSection(section.id);
      onDelete(section.id);
    } catch (err) {
      setError('Failed to delete section');
      console.error(err);
    }
  };

  const navigateToWorkshop = (workshopId) => {
    // Use the standard chat route - ChatPage will handle context loading
    navigate(`/chat/${workshopId}`);
  };

  if (!section) {
    return (
      <div className="section-detail section-detail--empty">
        <p>Select a section to view details</p>
      </div>
    );
  }

  return (
    <div className="section-detail">
      <div className="section-detail__header">
        <h3>{section.display_name}</h3>
        <span className="section-detail__type">{section.section_type}</span>
      </div>

      {section.description && (
        <p className="section-detail__description">{section.description}</p>
      )}

      {error && <div className="section-detail__error">{error}</div>}

      <div className="section-detail__actions">
        <Button
          onClick={handleGenerate}
          disabled={generating}
          variant="primary"
        >
          {generating ? 'Generating...' : section.generated_content ? 'Regenerate' : 'Generate'}
        </Button>
        <Button onClick={() => onCreateWorkshop(section)} variant="secondary">
          Add Workshop
        </Button>
        <Button onClick={handleDelete} variant="danger">
          Delete
        </Button>
      </div>

      <div className="section-detail__workshops">
        <h4>Workshop Sessions ({workshops.length})</h4>
        {loading ? (
          <Loading />
        ) : workshops.length === 0 ? (
          <p className="section-detail__no-workshops">
            No workshops yet. Create one to start brainstorming this section.
          </p>
        ) : (
          <ul className="section-detail__workshop-list">
            {workshops.map(workshop => (
              <li key={workshop.id} className="section-detail__workshop-item">
                <button
                  className="section-detail__workshop-link"
                  onClick={() => navigateToWorkshop(workshop.id)}
                >
                  {workshop.name}
                </button>
                <span className="section-detail__workshop-meta">
                  {workshop.message_count} messages
                  {workshop.has_summary && ' • Summarized'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {section.generated_content && (
        <div className="section-detail__content">
          <h4>Generated Content</h4>
          <div className="section-detail__content-text">
            {section.generated_content}
          </div>
          {section.generated_at && (
            <p className="section-detail__content-meta">
              Generated: {new Date(section.generated_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
      {paidDialog}
      <ConfirmModal {...modalProps} />
    </div>
  );
}

export default SectionDetail;
